import Vue from "vue";
import { createAuth0Client } from "@auth0/auth0-spa-js";

/** Define a default action to perform after authentication */
const DEFAULT_REDIRECT_CALLBACK = () =>
  window.history.replaceState({}, document.title, window.location.pathname);

let instance;

/** Returns the current instance of the SDK */
export const getInstance = () => instance;

/** Creates an instance of the Auth0 SDK. If one has already been created, it returns that instance */

export const useAuth0 = ({
  onRedirectCallback = DEFAULT_REDIRECT_CALLBACK,
  redirectUri = window.location.origin,
  ...options
}) => {
  // 20260128 -- Copilot (Claude Sonnet 4.5) -- Removed debug console.log
  if (instance) return instance;

  // The 'instance' is simply a Vue object
  instance = new Vue({
    data() {
      return {
        loading: true,
        isAuthenticated: false,
        user: {},
        auth0Client: null,
        popupOpen: false,
        error: null
      };
    },
    /** Use this lifecycle method to instantiate the SDK client */
    async created() {
      // 20260128 -- Copilot (Claude Sonnet 4.5) -- Removed console.log of Auth0 config
      // WHY: Exposes sensitive authentication configuration in browser console
      // Create a new instance of the SDK client using members of the given options object
      // 20260520 -- Copilot (Claude Sonnet 4.6) -- Added useRefreshTokens to fix iframe silent-auth failure on staging
      // WHY: Without this, getTokenSilently({cacheMode:'off'}) uses a hidden iframe (prompt=none), which modern
      //      browsers block via third-party cookie restrictions on real domains. Refresh token grant avoids iframes.
      this.auth0Client = await createAuth0Client({
        domain: options.domain,
        clientId: options.clientId,
        useRefreshTokens: true,
        authorizationParams: {
          audience: options.audience,
          redirect_uri: redirectUri
        }
      });

      try {
        // If the user is returning to the app after authentication..
        if (
          window.location.search.includes("code=") &&
          window.location.search.includes("state=")
        ) {
          // handle the redirect and retrieve tokens
          const { appState } = await this.auth0Client.handleRedirectCallback();

          // 20260405 -- Copilot (Claude Sonnet 4.6) -- Flag explicit login so authGuard can record lastLoggedIn
          // WHY: Silent token refresh also triggers authGuard; we must only record lastLoggedIn on real logins
          this.isFreshLogin = true;

          // Notify subscribers that the redirect callback has happened, passing the appState
          // (useful for retrieving any pre-authentication state)
          onRedirectCallback(appState);
        }
      } catch (e) {
        this.error = e;
      } finally {
        // Initialize our internal authentication state
        this.isAuthenticated = await this.auth0Client.isAuthenticated();
        this.user = await this.auth0Client.getUser();
        this.loading = false;
      }
    },
    methods: {
      /** Authenticates the user using a popup window */
      async loginWithPopup(o) {

        this.popupOpen = true;


        try {
          await this.auth0Client.loginWithPopup(o);
        } catch (e) {
          // eslint-disable-next-line
          console.error(e);
        } finally {
          this.popupOpen = false;
        }

        this.user = await this.auth0Client.getUser();
        this.isAuthenticated = true;
        // 20260405 -- Copilot (Claude Sonnet 4.6) -- Flag explicit popup login so authGuard can record lastLoggedIn
        this.isFreshLogin = true;
      },
      /** Handles the callback when logging in using a redirect */
      async handleRedirectCallback() {
        this.loading = true;
        try {
          await this.auth0Client.handleRedirectCallback();
          this.user = await this.auth0Client.getUser();
          this.isAuthenticated = true;
        } catch (e) {
          this.error = e;
        } finally {
          this.loading = false;
        }
      },
      /** Authenticates the user using the redirect method */
      loginWithRedirect(o) {
        // 20260128 -- Copilot (Claude Sonnet 4.5) -- Removed debug console.log statements
        return this.auth0Client.loginWithRedirect({
          ...o,
          authorizationParams: {
            ...o?.authorizationParams,
            redirect_uri: redirectUri
          }
        });
      },
      /** Returns all the claims present in the ID token */
      getIdTokenClaims(o) {
        return this.auth0Client.getIdTokenClaims(o);
      },
      /** Returns the access token. If the token is invalid or missing, a new one is retrieved */
      getTokenSilently(o) {
        return this.auth0Client.getTokenSilently(o);
      },
      /** Gets the access token using a popup window */

      getTokenWithPopup(o) {
        return this.auth0Client.getTokenWithPopup(o);
      },
      /** Logs the user out and removes their session on the authorization server */
      logout(o) {
        return this.auth0Client.logout(o);
      }
    }
  });

  return instance;
};

// Create a simple Vue plugin to expose the wrapper object throughout the application
export const Auth0Plugin = {
  install(app, options) {
    const authInstance = useAuth0(options);
    app.config.globalProperties.$auth = authInstance;
    app.provide('auth', authInstance);
  }
};